/**
 * FF Slider - Custom Element powered by Splide.js
 * 
 * Sync flow: Main sliders wait briefly for Navigation sliders before mounting.
 * This allows proper sync() before mount as required by Splide.
 */

if (!customElements.get("ff-slider")) {
  const sliderRegistry = new Map();
  const pendingMounts = new Map(); // Main sliders waiting to be mounted

  class FfSlider extends HTMLElement {
    connectedCallback() {
      if (typeof Splide === "undefined") {
        document.addEventListener("DOMContentLoaded", () => this.init());
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      const splideEl = this.querySelector(".splide");
      if (!splideEl || this.splide) return;

      // Mobile-only mode: skip Splide on desktop, CSS handles grid layout
      if (this.hasAttribute('data-ff-mobile-only')) {
        const breakpoint = parseInt(this.dataset.ffBreakpoint) || 768;
        if (window.innerWidth > breakpoint) {
          this.classList.remove("ff-slider--loading");
          return;
        }
      }

      this.autoWrapSlides();

      // Smart loop: disable loop if all slides fit in one view
      if (this.hasAttribute('data-ff-smart-loop')) {
        const breakpoint = parseInt(this.dataset.ffBreakpoint) || 768;
        const isMobile = window.innerWidth <= breakpoint;
        const rawConfig = JSON.parse(splideEl.dataset.splide || '{}');
        const bpOverrides = isMobile ? (rawConfig.breakpoints?.[String(breakpoint)] || {}) : {};
        const activeConfig = { ...rawConfig, ...bpOverrides };

        if (activeConfig.type === 'loop') {
          const slides = splideEl.querySelectorAll('.splide__list .splide__slide');
          const count = slides.length;
          let needsLoop = true;

          if (count > 0) {
            const containerWidth = splideEl.offsetWidth;
            const gap = parseFloat(activeConfig.gap) || 0;

            if (activeConfig.autoWidth) {
              const slideWidth = slides[0].offsetWidth;
              needsLoop = slideWidth > 0
                ? (count * slideWidth + (count - 1) * gap) > containerWidth
                : true;
            } else {
              const perPage = activeConfig.perPage || 1;
              // In center/peek mode, padding reduces the track width used to size slides.
              // Each slide = (trackWidth - gap*(perPage-1)) / perPage
              // Total slides width must fit within containerWidth (full visible area incl. peek)
              const padding = activeConfig.padding;
              let paddingPx = 0;
              if (padding) {
                if (typeof padding === 'string' && padding.includes('%')) {
                  paddingPx = containerWidth * parseFloat(padding) / 100;
                } else if (typeof padding === 'object') {
                  paddingPx = (parseFloat(padding.left || 0) + parseFloat(padding.right || 0)) / 2;
                } else {
                  paddingPx = parseFloat(padding) || 0;
                }
              }
              const trackWidth = containerWidth - 2 * paddingPx;
              const slideWidth = (trackWidth - gap * (perPage - 1)) / perPage;
              const totalWidth = count * slideWidth + (count - 1) * gap;
              needsLoop = totalWidth > containerWidth;
            }
          }

          if (!needsLoop) {
            const config = JSON.parse(splideEl.dataset.splide);
            config.type = 'slide';
            if (config.breakpoints) {
              Object.keys(config.breakpoints).forEach(bp => {
                if (config.breakpoints[bp].type === 'loop') {
                  config.breakpoints[bp].type = 'slide';
                }
              });
            }
            splideEl.dataset.splide = JSON.stringify(config);
          }
        }
      }

      // Store start index for after mount (using go() instead of start option for proper clamping)
      this.startIndex = parseInt(this.dataset.ffStartIndex) || 0;
      this.splide = new Splide(splideEl);
      this.setupProgressBar();

      const syncId = this.dataset.ffSync;

      if (syncId) {
        // This is a Navigation slider - needs to sync with a Main
        this.initAsNavigation(syncId);
      } else {
        // This could be a Main or standalone slider
        this.initAsMainOrStandalone();
      }
    }

    /**
     * Initialize as Navigation slider (syncs with a Main)
     */
    initAsNavigation(syncId) {
      const mainSlider = sliderRegistry.get(syncId);
      
      if (mainSlider && !mainSlider.isMounted) {
        // Main exists but not mounted yet - perfect, sync and mount together
        mainSlider.splide.sync(this.splide);
        mainSlider.splide.mount();
        this.splide.mount();
        mainSlider.onMounted();
        this.onMounted();
        
        // Clear any pending timeout for main
        if (pendingMounts.has(syncId)) {
          clearTimeout(pendingMounts.get(syncId));
          pendingMounts.delete(syncId);
        }
      } else if (mainSlider && mainSlider.isMounted) {
        // Main already mounted - use manual sync via events
        this.setupManualSync(mainSlider);
        this.splide.mount();
        this.onMounted();
      } else {
        // Main doesn't exist yet - register and wait
        this.pendingMainId = syncId;
        
        // Set timeout to mount anyway if main never appears
        setTimeout(() => {
          if (this.pendingMainId && !this.isMounted) {
            this.splide.mount();
            this.onMounted();
            this.pendingMainId = null;
          }
        }, 500);
      }
    }

    /**
     * Initialize as Main or standalone slider
     */
    initAsMainOrStandalone() {
      // Get the main ID (custom or element ID)
      const mainId = this.dataset.ffMainId;
      
      // Register with main ID if this is a "main" slider
      if (mainId) {
        sliderRegistry.set(mainId, this);
      }

      // Check if any Navigation sliders are waiting for us
      const waitingNavs = mainId ? this.findWaitingNavigations(mainId) : [];
      
      if (waitingNavs.length > 0) {
        // Sync with all waiting navigations and mount together
        waitingNavs.forEach(nav => {
          this.splide.sync(nav.splide);
          nav.pendingMainId = null;
        });
        
        this.splide.mount();
        this.onMounted();
        
        waitingNavs.forEach(nav => {
          nav.splide.mount();
          nav.onMounted();
        });
      } else if (mainId) {
        // This is a main slider - wait a bit for navigations
        const timeoutId = setTimeout(() => {
          if (!this.isMounted) {
            // Check one more time for navigations
            const lateNavs = this.findWaitingNavigations(mainId);
            if (lateNavs.length > 0) {
              lateNavs.forEach(nav => {
                this.splide.sync(nav.splide);
                nav.pendingMainId = null;
              });
              this.splide.mount();
              this.onMounted();
              lateNavs.forEach(nav => {
                nav.splide.mount();
                nav.onMounted();
              });
            } else {
              // No navigations, mount as standalone
              this.splide.mount();
              this.onMounted();
            }
          }
          pendingMounts.delete(mainId);
        }, 100);
        
        pendingMounts.set(mainId, timeoutId);
      } else {
        // Standalone slider - mount immediately
        this.splide.mount();
        this.onMounted();
      }
    }

    /**
     * Find Navigation sliders waiting to sync with this Main
     */
    findWaitingNavigations(mainId) {
      const navs = [];
      document.querySelectorAll(`ff-slider[data-ff-sync="${mainId}"]`).forEach(el => {
        if (el.pendingMainId === mainId && el.splide && !el.isMounted) {
          navs.push(el);
        }
      });
      return navs;
    }

    /**
     * Setup manual sync via events (fallback when Main already mounted)
     */
    setupManualSync(mainSlider) {
      // When main moves, move this navigation
      mainSlider.splide.on('move', (newIndex) => {
        if (this.splide && this.isMounted) {
          this.splide.go(newIndex);
        }
      });

      // When this navigation is clicked, move main
      this.splide.on('click', (slide) => {
        if (mainSlider.splide && mainSlider.isMounted) {
          mainSlider.splide.go(slide.index);
        }
      });
    }

    /**
     * Auto-wrap child elements in splide__slide if needed
     */
    autoWrapSlides() {
      const list = this.querySelector(".splide__list");
      if (!list) return;

      const children = Array.from(list.children);
      const needsWrapping = children.length > 0 && 
        !children[0].classList.contains('splide__slide') &&
        children[0].tagName !== 'LI';

      if (needsWrapping) {
        children.forEach((child) => {
          if (child.classList.contains('splide__slide')) return;
          const slide = document.createElement('li');
          slide.className = 'splide__slide';
          child.parentNode.insertBefore(slide, child);
          slide.appendChild(child);
        });
      }
    }

    /**
     * Setup progress bar functionality
     */
    setupProgressBar() {
      const progressBar = this.querySelector(".splide__progress__bar");
      if (!progressBar) return;

      this.splide.on("mounted move", () => {
        const end = this.splide.Components.Controller.getEnd() + 1;
        const current = this.splide.index + 1;
        const rate = Math.min(current / end, 1);
        const percentage = `${rate * 100}%`;
        
        // Check if vertical (direction is 'ttb' = top to bottom)
        const isVertical = this.splide.options.direction === 'ttb';
        
        if (isVertical) {
          progressBar.style.width = '';
          progressBar.style.height = percentage;
        } else {
          progressBar.style.height = '';
          progressBar.style.width = percentage;
        }
      });
    }

    /**
     * Enable horizontal touchpad/trackpad scrolling on slider
     * Allows two-finger horizontal swipe on Mac trackpads and similar gestures
     * without blocking vertical page scrolling
     */
    setupHorizontalWheelScroll() {
      let accumulatedDelta = 0;
      const threshold = 100; // Pixels needed to trigger slide change
      const cooldownTime = 350; // Time to ignore inertia events after slide change
      let resetTimeout = null;
      let cooldown = false; // Prevents inertia from triggering extra slides
      const track = this.querySelector('.splide__track');
      if (!track) return;

      track.addEventListener('wheel', (event) => {
        // Skip if Ctrl key is held (zoom gesture)
        if (event.ctrlKey) return;

        const deltaX = event.deltaX;
        const deltaY = event.deltaY;

        // Windows shift-scroll: treat vertical scroll as horizontal
        if (event.shiftKey && Math.abs(deltaX) === 0) {
          event.preventDefault();
          
          if (cooldown) return;
          
          accumulatedDelta += deltaY;
          
          if (Math.abs(accumulatedDelta) >= threshold) {
            if (accumulatedDelta > 0) {
              this.splide.go('>');
            } else {
              this.splide.go('<');
            }
            accumulatedDelta = 0;
            // Brief cooldown to ignore inertia events
            cooldown = true;
            setTimeout(() => { cooldown = false; }, cooldownTime);
          }
          return;
        }

        // Mac/trackpad sideways scroll
        if (!event.shiftKey && Math.abs(deltaX) !== 0) {
          // If scrolling more vertically than horizontally, let page scroll
          if (Math.abs(deltaX) < Math.abs(deltaY)) {
            return;
          }

          // Prevent default to stop browser back/forward navigation
          event.preventDefault();

          // Skip accumulation during cooldown (inertia period)
          if (cooldown) return;

          // Accumulate horizontal delta
          accumulatedDelta += deltaX;

          // Reset accumulated delta after inactivity (finger lifted)
          clearTimeout(resetTimeout);
          resetTimeout = setTimeout(() => {
            accumulatedDelta = 0;
          }, 150);

          // When threshold is reached, move to next/prev slide
          if (Math.abs(accumulatedDelta) >= threshold) {
            if (accumulatedDelta > 0) {
              this.splide.go('>');
            } else {
              this.splide.go('<');
            }
            accumulatedDelta = 0;
            // Brief cooldown to ignore inertia events after release
            cooldown = true;
            setTimeout(() => { cooldown = false; }, cooldownTime);
          }
        }
      }, { passive: false });
    }

    /**
     * Setup dynamic padding for peek/partial slides
     * Adjusts padding based on current slide position:
     * - First slide: padding on right (peek next)
     * - Last slide: padding on left (peek previous)
     */
    setupDynamicPadding() {
      const options = this.splide.options;
      const padding = options.padding;
      
      // Only apply if padding is configured
      if (!padding || (!padding.left && !padding.right)) return;
      
      const originalPadding = { ...padding };
      const track = this.querySelector('.splide__track');
      if (!track) return;

      const updatePadding = () => {
        const index = this.splide.index;
        const end = this.splide.Components.Controller.getEnd();
        
        // Get the padding value (could be on left or right)
        const paddingValue = originalPadding.right || originalPadding.left || '0';
        
        if (index >= end) {
          // Last slide: padding on left only
          track.style.paddingLeft = paddingValue;
          track.style.paddingRight = '0';
        } else {
          // First and middle slides: padding on both sides (half each)
          track.style.paddingLeft = '0';
          track.style.paddingRight = paddingValue;
        }
      };

      // Update on mount and on move
      this.splide.on('mounted move', updatePadding);
      updatePadding();
    }

    /**
     * Called after slider is mounted
     */
    onMounted() {
      this.isMounted = true;

      const isLoopMode = this.splide.options.type === 'loop';

      // Remove loading class first
      this.classList.remove("ff-slider--loading");

      // In loop mode, force refresh after removing loading class to recalculate clone positions
      if (isLoopMode) {
        requestAnimationFrame(() => {
          this.splide.refresh();
        });
      }

      // Navigate to start index after mount
      if (this.startIndex > 0) {
        requestAnimationFrame(() => {
          this.splide.go(this.startIndex);
        });
      }

      this.setupHorizontalWheelScroll();
      this.setupDynamicPadding();
      this.setupParentLinkBlock();
      this.setupModalSync();
      this.setupHeaderArrowsSync();
      // Run after layout so slide offsetWidths are accurate
      requestAnimationFrame(() => this.setupAutoWidthPagesCheck());
      this.dispatchEvent(new CustomEvent("ff-slider:init", { bubbles: true }));
    }

    /**
     * Sync slide changes with associated modal
     * When this slider moves, emit event so modal can update silently
     */
    setupModalSync() {
      const modalId = this.dataset.ffModalId;
      if (!modalId) return;

      this.splide.on('move', (newIndex) => {
        document.dispatchEvent(new CustomEvent('ff-gallery-modal:sync', {
          detail: { modalId, index: newIndex + 1 }
        }));
      });
    }

    /**
     * Sync disabled state from actual button arrows to header label arrows
     * Header arrows are <label> elements that click the real <button> arrows
     * Splide only sets disabled on buttons, so we mirror state to labels
     */
    setupHeaderArrowsSync() {
      const headerArrows = this.querySelectorAll('.ff-slider__header-arrows .splide__arrow');
      if (headerArrows.length === 0) return;

      // Find the actual button arrows by ID (labels have for="id")
      const prevLabel = this.querySelector('.ff-slider__header-arrows .splide__arrow--prev');
      const nextLabel = this.querySelector('.ff-slider__header-arrows .splide__arrow--next');
      if (!prevLabel || !nextLabel) return;

      const prevButton = document.getElementById(prevLabel.getAttribute('for'));
      const nextButton = document.getElementById(nextLabel.getAttribute('for'));
      if (!prevButton || !nextButton) return;

      // Sync function to mirror disabled state
      const syncDisabledState = () => {
        prevLabel.classList.toggle('is-disabled', prevButton.disabled);
        nextLabel.classList.toggle('is-disabled', nextButton.disabled);
      };

      // Initial sync
      syncDisabledState();

      // Watch for attribute changes on buttons
      const observer = new MutationObserver(syncDisabledState);
      observer.observe(prevButton, { attributes: true, attributeFilter: ['disabled'] });
      observer.observe(nextButton, { attributes: true, attributeFilter: ['disabled'] });

      // Store observer for cleanup
      this._headerArrowsObserver = observer;
    }

    /**
     * Block parent link interaction when hovering over slider
     * Activated by data-ff-block-parent-link attribute
     * Optional value: CSS selector to find the target element (default: 'a')
     * Example: data-ff-block-parent-link=".product-card__link"
     */
    setupParentLinkBlock() {
      if (!this.hasAttribute('data-ff-block-parent-link')) return;
      
      const selector = this.dataset.ffBlockParentLink || 'a';
      const targetElement = this.closest(selector);
      if (!targetElement) return;

      this.addEventListener('mouseenter', () => {
        targetElement.classList.add('ff-pointer-events-none');
      });
      this.addEventListener('mouseleave', () => {
        targetElement.classList.remove('ff-pointer-events-none');
      });
      this.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    }

    /**
     * Hide arrows when all slides fit in one view (autoWidth mode only)
     * Checks the active options for the current breakpoint — if autoWidth is
     * not active right now the method is a no-op.
     */
    setupAutoWidthPagesCheck() {
      const options = this.splide.options;
      if (!options.autoWidth || options.type === 'loop') return;

      const slides = this.splide.Components.Elements.slides; // real slides, no clones
      if (!slides || slides.length === 0) return;

      const gap = parseFloat(options.gap) || 0;
      const totalSlidesWidth = slides.reduce((sum, s) => sum + s.offsetWidth, 0)
        + Math.max(0, slides.length - 1) * gap;

      const containerWidth = (this.querySelector('.splide') || this).offsetWidth;

      // Use bleed/overflow padding from dataset (set via ff_slider_overflow_padding setting)
      // NOT options.padding — that's the Splide peek % and is unrelated to physical bleed
      const isMobile = window.innerWidth <= parseInt(this.dataset.ffBreakpoint || 997);
      const overflowPadding = isMobile
        ? parseInt(this.dataset.ffOverflowPaddingMb || this.dataset.ffOverflowPadding || 0)
        : parseInt(this.dataset.ffOverflowPadding || 0);

      const allFit = totalSlidesWidth <= containerWidth - overflowPadding * 2;

      // Desktop and mobile are toggled independently with their own hidden classes.
      // if the real buttons are already hidden by Liquid must stay that way.
      const headerArrowsDiv = this.querySelector('.ff-slider__header-arrows');
      const hiddenClass = isMobile ? 'splide__arrow--hidden-mb' : 'splide__arrow--hidden';
      const headerHiddenClass = isMobile ? 'ff-slider__header-arrows--hidden-mb' : 'ff-slider__header-arrows--hidden';
      const isHeaderMode = headerArrowsDiv && !headerArrowsDiv.classList.contains(headerHiddenClass);

      if (!isHeaderMode) {
        this.querySelectorAll('.splide__arrows .splide__arrow').forEach(arrow => {
          arrow.classList.toggle(hiddenClass, allFit);
        });
      } else {
        this.querySelectorAll('label.splide__arrow').forEach(arrow => {
          arrow.classList.toggle(hiddenClass, allFit);
        });
      }
    }

    destroy() {
      const mainId = this.dataset.ffMainId;
      if (mainId) {
        sliderRegistry.delete(mainId);
        if (pendingMounts.has(mainId)) {
          clearTimeout(pendingMounts.get(mainId));
          pendingMounts.delete(mainId);
        }
      }
      // Cleanup header arrows observer
      if (this._headerArrowsObserver) {
        this._headerArrowsObserver.disconnect();
        this._headerArrowsObserver = null;
      }
      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
        this.dispatchEvent(new CustomEvent("ff-slider:destroy", { bubbles: true }));
      }
    }

    refresh() {
      if (this.splide) {
        this.splide.refresh();
      }
    }

    go(index) {
      if (this.splide) {
        this.splide.go(index);
      }
    }

    get index() {
      return this.splide ? this.splide.index : 0;
    }

    get length() {
      return this.splide ? this.splide.length : 0;
    }
  }

  customElements.define("ff-slider", FfSlider);
}
